// The event modal is URL-driven, in one of two forms:
//
// - Over the activity feed, the canonical shareable path form is used
//   (/events/:deviceId/:eventId), so the address bar matches the share URL.
//   The feed lives at /events precisely so both URLs resolve to the same
//   routed view in the tab outlet and the feed stays mounted underneath.
//   Deep links, shared URLs, and push notifications land on this form and
//   present straight over the feed.
// - Over any other page, the event lives in an `event` search param
//   (e.g. /devices/OC-X?event=OC-X%3A301): search params don't participate
//   in route matching, so the origin page stays mounted with its scroll
//   intact while the modal is up.

const EVENT_PARAM = 'event';
const TOKEN_PARAM = 't';

const EVENT_PATH_PATTERN = /^\/events\/([^/]+)\/(\d+)$/;

export interface EventModalTarget {
    deviceId: string;
    eventId: number;
    accessToken: string | null;
}

/** Location that opens the event modal over the given base location. */
export function eventModalHref(
    base: { pathname: string; search: string },
    deviceId: string,
    eventId: number | string,
    accessToken?: string | null
): string {
    if (base.pathname === '/events' || EVENT_PATH_PATTERN.test(base.pathname)) {
        return `/events/${deviceId}/${eventId}${accessToken ? `?${TOKEN_PARAM}=${encodeURIComponent(accessToken)}` : ''}`;
    }

    const params = new URLSearchParams(base.search);
    params.set(EVENT_PARAM, `${deviceId}:${eventId}`);
    if (accessToken) {
        params.set(TOKEN_PARAM, accessToken);
    } else {
        // a token authorises one event only; never carry it to another
        params.delete(TOKEN_PARAM);
    }
    return `${base.pathname}?${params.toString()}`;
}

/** The location the modal's base page owns, i.e. where closing returns to. */
export function clearEventModalHref(base: { pathname: string; search: string }): string {
    if (EVENT_PATH_PATTERN.test(base.pathname)) {
        return '/events';
    }

    const params = new URLSearchParams(base.search);
    params.delete(EVENT_PARAM);
    params.delete(TOKEN_PARAM);
    const search = params.toString();
    return `${base.pathname}${search ? `?${search}` : ''}`;
}

/** Parses the event modal target out of a location, if present and well-formed. */
export function matchEventModalLocation(location: { pathname: string; search: string }): EventModalTarget | null {
    const params = new URLSearchParams(location.search);

    const pathMatch = location.pathname.match(EVENT_PATH_PATTERN);
    if (pathMatch) {
        return {
            deviceId: pathMatch[1],
            eventId: parseInt(pathMatch[2]),
            accessToken: params.get(TOKEN_PARAM)
        };
    }

    const eventParam = params.get(EVENT_PARAM);
    if (!eventParam) {
        return null;
    }

    const separator = eventParam.lastIndexOf(':');
    if (separator <= 0) {
        return null;
    }

    const deviceId = eventParam.slice(0, separator);
    const eventId = parseInt(eventParam.slice(separator + 1));
    if (isNaN(eventId)) {
        return null;
    }

    return { deviceId, eventId, accessToken: params.get(TOKEN_PARAM) };
}
